import { IToastSize, IToastType } from '@/../typings';
import { IActionCategory, IComponentType, IFaceLandmark, IScreenAnchorPositionType, ITrackingTypes, ITriggerTypes, ROOT_COMPNENT_ID, } from '@r3f-component-data-structure';
import { getFaceLandmarkIdsBySceneId } from '@/components/r3f/r3f-components/hooks/useGetScreenRelativeComponentIds';
import { store } from '@/store';
import { DEFAULT_SCENE_NAME } from '@/utils/constants';
import { getImageTrackerIdByStartSceneId, getSceneIdsByTrackerId, getSnapshotDictFromLStorage, isSceneActiveImageTrackerStartScene, symmetricDifference, updateLStorageWithSnapshot, } from '@/utils/general';
import { getActionDataWithReferencingPivots, getChildlessAbstractComponentIds } from '@/utils/pure';
import { getEntityDropTitle, getSceneTitles } from '@/utils/pure/get';
import { MathUtils } from 'three';
import { IContentDocActionTypes, IUserActionTypes, } from './action-types';
export const onRemoveEntities_Global = (entityIds, onlyAbstract = false) => {
    return (dispatch) => {
        if (!onlyAbstract) {
            // If only abstract then don't deselect the spatial entity if there is one selected
            dispatch({
                type: IUserActionTypes.REMOVE_LOCAL_USER_SELECTION,
            });
        }
        dispatch({
            type: IUserActionTypes.DISABLE_UNDO_BTN,
            payload: false,
        });
        dispatch({
            type: IContentDocActionTypes.REMOVE_ENTITIES_CN_DOC,
            payload: { ids: entityIds },
        });
        const { componentsById } = store.getState().contentReducer.contentDoc;
        const childlessAbstractComponentIds = getChildlessAbstractComponentIds(componentsById);
        dispatch({
            type: IContentDocActionTypes.REMOVE_ENTITIES_CN_DOC,
            payload: { ids: childlessAbstractComponentIds },
        });
        if (!onlyAbstract) {
            const actionData = getActionDataWithReferencingPivots(entityIds, componentsById);
            for (let i = 0; i < actionData.length; i++) {
                dispatch({
                    type: IContentDocActionTypes.REMOVE_COMPONENT_ACTION_CN_DOC,
                    payload: actionData[i],
                });
            }
        }
    };
};
export const onReplaceEntity_Global = (currentlySelectedEntityId, replacementEntity) => {
    const entity = store.getState().contentReducer.contentDoc.componentsById[currentlySelectedEntityId];
    entity.id = currentlySelectedEntityId;
    return (dispatch) => {
        dispatch({
            type: IContentDocActionTypes.REPLACE_ENTITY_CN_DOC,
            payload: {
                entity,
                replacementEntity,
            },
        });
    };
};
export const onAddEntity_Global = ({ parentId, entity, disableAutoSelection }) => {
    return async (dispatch) => {
        const activeSceneId = store.getState().userReducer.activeSceneId;
        const { componentsById } = store.getState().contentReducer.contentDoc;
        // If parentId is a ScreenAnchorPositionType then we're adding to a SR layer
        const isScreenRelative = Object.values(IScreenAnchorPositionType).includes(parentId);
        // If parentId is a FaceLandmark then we're adding to an AR layer on a Face Tracked scene
        const isFaceTrackingAr = Object.values(IFaceLandmark).includes(parentId);
        if (isScreenRelative) {
            const screenContentId = componentsById[activeSceneId].children.filter((id) => componentsById[id].type === IComponentType.ScreenContent)[0];
            const existingScreenContent = componentsById[screenContentId];
            const isScreenRelativeAnchorGroupExists = componentsById[screenContentId].children.filter((id) => componentsById[id].type === IComponentType.ScreenAnchorGroup && componentsById[id].anchorPositionType === parentId).length > 0;
            if (isScreenRelativeAnchorGroupExists) {
                // Get tht group's ID and add to that group
                const existingAnchorGroupId = componentsById[screenContentId].children.filter((id) => componentsById[id].type === IComponentType.ScreenAnchorGroup && componentsById[id].anchorPositionType === parentId)[0];
                const existingAnchorGroup = componentsById[existingAnchorGroupId];
                dispatch({
                    type: IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC,
                    payload: [
                        {
                            ...existingAnchorGroup,
                            id: existingAnchorGroupId,
                            children: [...existingAnchorGroup.children, entity.id],
                        },
                        {
                            ...entity,
                            id: entity.id,
                        },
                    ],
                });
            }
            else {
                const newId = MathUtils.generateUUID();
                const newAnchorGroup = {
                    anchorPositionType: parentId,
                    type: IComponentType.ScreenAnchorGroup,
                    children: [entity.id],
                };
                dispatch({
                    type: IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC,
                    payload: [
                        {
                            ...existingScreenContent,
                            id: screenContentId,
                            children: [...existingScreenContent.children, newId],
                        },
                        {
                            ...newAnchorGroup,
                            id: newId,
                        },
                        {
                            ...entity,
                            id: entity.id,
                        },
                    ],
                });
            }
        }
        if (isFaceTrackingAr) {
            const isFaceLandmarkExists = componentsById[activeSceneId].children.filter((id) => componentsById[id].type === IComponentType.FaceLandmarkGroup && componentsById[id].landmark === parentId).length > 0;
            if (isFaceLandmarkExists) {
                // Get tht group's ID and add to that group
                const existingLandmarkGroupId = componentsById[activeSceneId].children.filter((id) => componentsById[id].type === IComponentType.FaceLandmarkGroup && componentsById[id].landmark === parentId)[0];
                const existingLandmarkGroup = componentsById[existingLandmarkGroupId];
                dispatch({
                    type: IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC,
                    payload: [
                        {
                            ...existingLandmarkGroup,
                            id: existingLandmarkGroupId,
                            children: [...existingLandmarkGroup.children, entity.id],
                        },
                        {
                            ...entity,
                            id: entity.id,
                        },
                    ],
                });
            }
            else {
                const newId = MathUtils.generateUUID();
                const existingScene = JSON.parse(JSON.stringify(componentsById[activeSceneId]));
                const newLandmarkGroup = {
                    landmark: parentId,
                    type: IComponentType.FaceLandmarkGroup,
                    children: [entity.id],
                };
                dispatch({
                    type: IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC,
                    payload: [
                        {
                            ...existingScene,
                            id: activeSceneId,
                            children: [...existingScene.children, newId],
                        },
                        {
                            ...newLandmarkGroup,
                            id: newId,
                        },
                        {
                            ...entity,
                            id: entity.id,
                        },
                    ],
                });
            }
        }
        if (!isFaceTrackingAr && !isScreenRelative) {
            // If not face tracking or SR just add entity to parent id
            dispatch({
                type: IContentDocActionTypes.ADD_ENTITY_CN_DOC,
                payload: {
                    parentId,
                    entity,
                },
            });
        }
        dispatch({
            type: IUserActionTypes.REMOVE_LOCAL_USER_SELECTION,
        });
        if (!disableAutoSelection) {
            setTimeout(() => {
                dispatch({
                    type: IUserActionTypes.ADD_ENTITY_IDS_TO_SELECTION,
                    payload: [entity.id],
                });
            }, 700);
        }
    };
};
export const onCopyScenesAtIndex_Global = ({ projectId, sceneIds, index }) => {
    return (dispatch, getState) => {
        dispatch({
            type: IUserActionTypes.SET_SCENE_SNAPSHOTS,
            payload: getSnapshotDictFromLStorage(localStorage, projectId),
        });
        const rootChildrenBefore = getState().contentReducer.contentDoc.componentsById[ROOT_COMPNENT_ID].children;
        dispatch({
            type: IContentDocActionTypes.COPY_SCENES_AT_INDEX_CN_DOC,
            payload: { sceneIds, index, adjustPositionToCamera: false },
        });
        setTimeout(() => {
            const rootChildrenAfter = getState().contentReducer.contentDoc.componentsById[ROOT_COMPNENT_ID].children;
            const newSceneId = Array.from(symmetricDifference(new Set(rootChildrenBefore), new Set(rootChildrenAfter)))[0];
            dispatch({
                type: IUserActionTypes.SET_ACTIVE_SCENE,
                payload: { activeSceneId: newSceneId },
            });
            updateLStorageWithSnapshot(newSceneId, projectId);
        }, 0);
    };
};
export const onAddScenes_Global = ({ sceneTrackingType }) => {
    return (dispatch, getState) => {
        const componentDict = getState().contentReducer?.contentDoc?.componentsById;
        const rootId = getState().contentReducer?.contentDoc?.rootComponentId;
        const rootChildren = [...(componentDict?.[rootId]?.children || [])];
        const isFirstLoad = rootChildren.length === 0;
        const index = isFirstLoad ? getState().userReducer.currentSceneIndex : getState().userReducer.currentSceneIndex + 1;
        const projectId = getState().userReducer.project.id;
        const sceneTitles = getSceneTitles(JSON.parse(JSON.stringify(componentDict)));
        const prospectiveTitle = getEntityDropTitle(sceneTitles, `${DEFAULT_SCENE_NAME} 1`, true);
        // Deal with this edge case here rather than confuse the getEntityDropTitle further
        const title = prospectiveTitle == 'Scene' ? 'Scene 1' : prospectiveTitle;
        const newScreenContentId = MathUtils.generateUUID();
        const newSceneId = MathUtils.generateUUID();
        rootChildren.splice(index, 0, newSceneId);
        const newSceneData = {
            id: newSceneId,
            type: IComponentType.Scene,
            title,
            trackingType: sceneTrackingType,
            children: [newScreenContentId],
        };
        // If image tracked scene, add default show target lost prompt action
        if (sceneTrackingType === ITrackingTypes.image) {
            newSceneData.actions = {
                [ITriggerTypes.onTargetImageLost]: [{ type: IActionCategory.showTargetLostPrompt }]
            };
        }
        dispatch({
            type: IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC,
            payload: [
                {
                    id: rootId,
                    children: rootChildren,
                },
                newSceneData,
                {
                    id: newScreenContentId,
                    type: IComponentType.ScreenContent,
                    children: [],
                },
            ],
        });
        if (sceneTrackingType === ITrackingTypes.world) {
            dispatch({
                type: IUserActionTypes.SET_3D_MODE,
                payload: true,
            });
        }
        // Set current scene to new one
        dispatch({
            type: IUserActionTypes.SET_ACTIVE_SCENE,
            payload: {
                activeSceneId: newSceneId,
            },
        });
        // Increment for next add scene action
        dispatch({
            type: IUserActionTypes.SET_CURRENT_SCENE_INDEX,
            payload: index,
        });
        if (!isFirstLoad) {
            //	Remove anything selected as we'll be on new scene
            dispatch({
                type: IUserActionTypes.REMOVE_SELECTION,
            });
            // Only applicable when adding additional scene
            dispatch({
                type: IUserActionTypes.SET_SCENE_MENU_LARGE,
                payload: true,
            });
            if (projectId) {
                setTimeout(() => {
                    updateLStorageWithSnapshot(newSceneId, projectId, sceneTrackingType === ITrackingTypes.noTrackingScreen); // Add snapshot to local storage
                    dispatch({
                        type: IUserActionTypes.SET_SCENE_SNAPSHOTS,
                        payload: getSnapshotDictFromLStorage(localStorage, projectId),
                    });
                }, 1000);
            }
        }
    };
};
export const onRemoveScenes_Global = (sceneIds) => {
    const rootId = store.getState().contentReducer.contentDoc.rootComponentId;
    const componentDict = store.getState().contentReducer.contentDoc.componentsById;
    const imageTrackingDict = store.getState().contentReducer.contentDoc?.tracking?.image;
    const rootComponent = componentDict[rootId];
    return (dispatch, getState) => {
        if (rootComponent.children.length === 1)
            return;
        let componentIdsToDelete = sceneIds; // delete scenes
        for (let i = 0; i < sceneIds.length; i++) {
            let screenRelativeComponents = [];
            const sceneChildren = componentDict[sceneIds[i]].children;
            const sceneSpatialChildren = sceneChildren.filter((id) => componentDict[id].type !== IComponentType.ScreenContent && componentDict[id].type !== IComponentType.FaceLandmarkGroup);
            const sceneScreenContentId = sceneChildren.filter((id) => componentDict[id].type === IComponentType.ScreenContent)[0]; // Only 1
            const anchorGroupIds = componentDict[sceneScreenContentId].children;
            anchorGroupIds.forEach((anchorGroupId) => {
                const children = componentDict[anchorGroupId].children;
                screenRelativeComponents = [...screenRelativeComponents, ...children];
            });
            screenRelativeComponents = [...screenRelativeComponents, ...anchorGroupIds, sceneScreenContentId];
            // Face tracking scenes
            const isFaceTrackingScene = componentDict[sceneIds[i]].type === IComponentType.Scene && componentDict[sceneIds[i]].trackingType === ITrackingTypes.face;
            let faceTrackingComponents = [];
            if (isFaceTrackingScene) {
                const faceLandmarkGroupIds = getFaceLandmarkIdsBySceneId(sceneIds[i], componentDict);
                faceLandmarkGroupIds.forEach((faceLandmarkId) => {
                    const children = componentDict[faceLandmarkId].children;
                    faceTrackingComponents = [...faceTrackingComponents, ...children];
                });
                faceTrackingComponents = [...faceTrackingComponents, ...faceLandmarkGroupIds];
            }
            componentIdsToDelete = [...componentIdsToDelete, ...sceneSpatialChildren, ...screenRelativeComponents, ...faceTrackingComponents]; // delete entities in scenes
        }
        const firstSceneSelected = getState().userReducer.activeSceneId === rootComponent.children[0];
        const indexOfFirstDeletedScene = rootComponent.children.indexOf(sceneIds[0]);
        const newActiveSceneIndex = firstSceneSelected ? 1 : indexOfFirstDeletedScene - 1;
        for (let i = 0; i < sceneIds.length; i++) {
            const sceneId = sceneIds[i];
            const isStartScene = isSceneActiveImageTrackerStartScene(imageTrackingDict, componentDict, sceneId, rootId);
            const scene = componentDict[sceneId];
            if (scene.trackingType === ITrackingTypes.image && !isStartScene)
                continue;
            const trackerIdWithSceneAsStartScene = getImageTrackerIdByStartSceneId(imageTrackingDict, sceneId);
            const sceneIdsWithTrackerId = getSceneIdsByTrackerId(componentDict, rootId, trackerIdWithSceneAsStartScene);
            if (sceneIdsWithTrackerId.length === 1)
                continue;
            const indexOfSceneToDelete = sceneIdsWithTrackerId.indexOf(sceneId);
            // If scene to delete is last scene to use tracker id, start from beginning
            const nextSceneIdWithTrackerId = sceneIdsWithTrackerId[indexOfSceneToDelete + 1] ?? sceneIdsWithTrackerId[0];
            if (!trackerIdWithSceneAsStartScene && !nextSceneIdWithTrackerId)
                continue;
            dispatch({
                type: IContentDocActionTypes.SET_IMAGE_TRACKING_CN_DOC,
                payload: { id: trackerIdWithSceneAsStartScene, startSceneId: nextSceneIdWithTrackerId },
            });
        }
        dispatch({
            type: IUserActionTypes.REMOVE_LOCAL_USER_SELECTION,
        });
        dispatch({
            type: IUserActionTypes.DISABLE_UNDO_BTN,
            payload: false,
        });
        dispatch({
            type: IUserActionTypes.SET_ACTIVE_SCENE,
            payload: { activeSceneId: rootComponent.children[newActiveSceneIndex] },
        });
        dispatch({
            type: IUserActionTypes.SET_CURRENT_SCENE_INDEX,
            payload: newActiveSceneIndex,
        });
        dispatch({
            type: IContentDocActionTypes.REMOVE_ENTITIES_CN_DOC,
            payload: { ids: componentIdsToDelete },
        });
        dispatch({
            type: IContentDocActionTypes.REMOVE_SCENE_REFERENCES_CN_DOC,
            payload: { sceneIds },
        });
    };
};
export const onPasteCopiedEntities_Global = (pld) => {
    const { copiedIds } = pld;
    // If user has copied a 3D model to a Screen scene, this is invalid
    const isScreenRelativeMode = store.getState().userReducer.isScreenRelativeMode;
    const componentsById = store.getState().contentReducer.contentDoc.componentsById;
    const activeSceneId = store.getState().userReducer.activeSceneId;
    const isTargetSceneImageTracked2d = !store.getState().userReducer.is3dMode && store.getState().contentReducer.contentDoc.componentsById[String(activeSceneId)].trackingType === ITrackingTypes.image;
    const isPasteContains3dContent = copiedIds.reduce((acc, id) => {
        return acc || componentsById[id].type === IComponentType.Model3d || componentsById[id].type === IComponentType.Emitter || componentsById[id].type === IComponentType.Text3d;
    }, false);
    // Loop over all IDs and if SR mode and TYPE is Model3d then filter out
    const filteredCopiedIds = isScreenRelativeMode
        ? copiedIds.filter((id) => {
            return componentsById[id].type !== IComponentType.Model3d && componentsById[id].type !== IComponentType.Emitter && componentsById[id].type !== IComponentType.Text3d;
        })
        : copiedIds;
    if (filteredCopiedIds.length == 0) {
        // If this leaves us with an empty array ( all models - in practice just 1 anyway ) then return a thunk that pops a toast ONLY
        return (dispatch) => {
            dispatch({
                type: IUserActionTypes.ADD_TOAST,
                payload: {
                    type: IToastType.info,
                    message: 'Cannot paste a 3D model, 3D Text or particle emitter onto a screen relative scene.',
                    size: IToastSize.default,
                    timeout: 5000,
                    closable: true,
                },
            });
        };
    }
    const cnDocPayload = { ...pld, isScreenRelativeMode };
    return (dispatch) => {
        dispatch({
            type: IContentDocActionTypes.PASTE_COPIED_ENTITIES_CN_DOC,
            payload: cnDocPayload,
        });
        if (isTargetSceneImageTracked2d && isPasteContains3dContent) {
            dispatch({
                type: IUserActionTypes.SET_3D_MODE,
                payload: true,
            });
        }
    };
};
